import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { decisionsKeys } from "@/application/query-keys/decisions.keys";
import {
  decisionsRepository,
  type DecisionFilters,
} from "@/infrastructure/repositories/decisions.supabase.repository";
import type { DecisionRow } from "@/infrastructure/supabase/database.types";

export function useDecisionsList(filters: DecisionFilters = {}) {
  return useQuery({
    queryKey: decisionsKeys.list(filters),
    queryFn: () => decisionsRepository.list(filters),
  });
}

export function useDecisionDetail(id: string | undefined) {
  return useQuery({
    queryKey: decisionsKeys.detail(id ?? ""),
    queryFn: () => decisionsRepository.byId(id as string),
    enabled: !!id,
  });
}

export function useCreateDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<DecisionRow, "id" | "created_at" | "updated_at">) =>
      decisionsRepository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: decisionsKeys.all });
    },
  });
}

export function useUpdateDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<DecisionRow> }) =>
      decisionsRepository.update(id, patch),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: decisionsKeys.all });
      queryClient.setQueryData(decisionsKeys.detail(data.id), data);
    },
  });
}

export function useDeleteDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => decisionsRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: decisionsKeys.all });
    },
  });
}
